import CheckIcon from '@mui/icons-material/Check';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const PenaltiesNonprofit = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Penalties for Nonprofits | TaxZerone"
          description="Avoid IRS penalties for late or incorrect Form 990 filings. Learn the common reasons, fines, and how to stay compliant with TaxZerone e-file solution."
          keywords=""
          canonical="resources/tax-exempt/form-990-penalties/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Understanding Forms 990:{' '}
          <span className="highlight-container">
            <span className="highlight">Penalties </span>
          </span>{' '}
          <br /> for Nonprofit Organizations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your 990 forms on time with TaxZerone to avoid penalties!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=penalties`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-penalties/"
                  />

                  {/* Article image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Penalties at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Late Filing Penalties:</b> Organizations that file
                        late, submit incomplete returns, or fail to meet IRS
                        filing requirements may be subject to monetary penalties
                        based on the applicable form and IRS rules.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Form-Specific Rules:</b> Penalties vary by return
                        type.{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Forms 990
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          990-EZ
                        </Link>
                        , and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-pf/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          990-PF
                        </Link>{' '}
                        generally incur daily late-filing penalties, while{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-T
                        </Link>{' '}
                        penalties depend on unpaid tax.{' '}
                        <Link
                          href="https://epostcard.taxzerone.com/?ref=menu&_gl=1*1pkxk5k*_gcl_au*ODg5ODU2MDY2LjE3ODQwOTMzNTg."
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-N
                        </Link>{' '}
                        has no monetary late-filing penalty but may result in
                        automatic revocation after three consecutive missed
                        filings.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Penalty Relief:</b> The IRS may waive penalties if an
                        organization demonstrates <b>reasonable cause</b> and
                        satisfies the applicable relief requirements.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonprofit organizations must file Form 990 with the Internal
                  Revenue Service every year to stay compliant and maintain
                  their tax-exempt status. Filing on time ensures transparency
                  in financial reporting, governance, and operations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Missing Form 990 deadlines or filing incorrect returns can
                  result in penalties. This resource explains the penalties for
                  late, incomplete, or non-filed returns, along with key
                  compliance requirements.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common reasons nonprofits may face Form 990 penalties
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#f990" sx={{ color: '#0202ff' }}>
                          Penalties for late filing of Form 990
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#f990t" sx={{ color: '#0202ff' }}>
                          Penalties for late filing of Form 990-T
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#tax" sx={{ color: '#0202ff' }}>
                          Penalties related to tax payments (Form 990-PF &
                          990-T)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#person" sx={{ color: '#0202ff' }}>
                          Penalties for responsible persons
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#add" sx={{ color: '#0202ff' }}>
                          Additional consequences and penalties
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#relief" sx={{ color: '#0202ff' }}>
                          Penalty relief and exceptions
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing 990 forms with TaxZerone to avoid penalties
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sources" sx={{ color: '#0202ff' }}>
                          Source and References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Reasons Nonprofits May Face Form 990 Penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to file on time
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The most common issue is filing late or not filing at all,
                  even after requesting an extension.
                </Typography>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#f4d9e1',
                    borderRadius: 2,
                    px: 1.5,
                    py: 2,
                    borderLeft: '3px solid #c22d57',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontStyle: 'italic' }}
                  >
                    <span style={{ fontWeight: 600 }}> Example:</span>{' '}
                    Calendar-year organizations must file by May 15 unless
                    extended.
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Filing incorrect or incomplete information
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Errors such as wrong EIN, missing financial data, or required
                  schedules can trigger penalties.
                </Typography>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: '#f4d9e1',
                    borderRadius: 2,
                    px: 1.5,
                    py: 2,
                    borderLeft: '3px solid #c22d57',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 0, fontStyle: 'italic' }}
                  >
                    <span style={{ fontWeight: 600 }}> Example:</span> Missing
                    Schedule A for public charities.
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to sign and authorize the form
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Returns must be signed by an authorized officer or trustee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to e-file when required
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most nonprofits are required to file electronically unless a
                  waiver is granted.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to make Form 990 publicly available
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations must provide copies for public inspection upon
                  request.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to pay taxes or estimated taxes on time
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Applicable for forms like 990-PF and 990-T.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Failure to correct errors after IRS notification
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Ignoring IRS notices can lead to additional penalties.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="f990"
                >
                  Penalties for Late Filing of Form 990
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Penalties depend on the organization’s gross receipts and are
                  subject to IRS inflation adjustments.
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Organization Size
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Penalty Per Day
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Maximum Penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Small Organizations (Gross receipts ≤ $1,309,500)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $25/day
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            $13,000 or 5% of gross receipts
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Large Organizations (Gross receipts &gt; $1,309,500)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $130/day
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            $65,000 or 5% of gross receipts
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b>{' '}
                  <ul style={{ marginRight: 18 }}>
                    <li>
                      Applies to{' '}
                      <Link
                        href="/tax-exempt/e-file-irs-form-990/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        irs Form 990
                      </Link>
                      ,{' '}
                      <Link
                        href="/tax-exempt/e-file-irs-form-990-ez/"
                        sx={{ color: '#0202ff' }}
                      >
                        form 990-EZ
                      </Link>
                      , and 990-PF{' '}
                    </li>
                    <li>
                      No direct monetary penalty for 990-N, but failure to file
                      for 3 consecutive years results in automatic
                      revocation.{' '}
                    </li>
                  </ul>
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="f990t"
                >
                  Penalties for Late Filing of Form 990-T
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations filing{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990-T
                  </Link>{' '}
                  for unrelated business income may face:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Penalty Description
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Rate/Amount
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Maximum Penalty
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Late filing penalty for unpaid tax
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> 5%</span> of
                            unpaid tax per month (or part of a month)
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Up to <span style={{ fontWeight: 600 }}>25%</span>{' '}
                            of unpaid tax
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Return filed{' '}
                            <span style={{ fontWeight: 600 }}>
                              more than 60 days
                            </span>{' '}
                            late
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Minimum penalty applies
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            Lesser of{' '}
                            <span style={{ fontWeight: 600 }}> $525</span> +
                            (adjusted annually) or 100% of the unpaid tax
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Penalties Related to Tax Payments (Form 990-PF and Form 990-T)
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Late tax payment penalty:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  0.5% of unpaid tax per month, up to 25%
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Failure to make estimated tax payments:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Applies if tax liability is $500 or more (typically for 990-T
                  filers). Quarterly estimated payments may be required.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Trust fund recovery penalty:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Equal to 100% of unpaid trust fund taxes (e.g., Social
                  Security and Medicare), if not properly collected or paid.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="person"
                >
                  Penalties for Responsible Persons
                </Typography>
                <Typography variant="body1">
                  If the IRS requires corrections and the responsible person
                  fails to act:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>$10 per day penalty</li>
                  <li>Maximum up to $6,500 per return</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization fails to file a complete return or provide
                  correct information, the IRS will issue a letter with a fixed
                  deadline to comply. Failure to meet this deadline results in
                  the above penalties. Responsible persons include officers,
                  directors, or individuals responsible for filing compliance.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="add"
                >
                  Additional Consequences and Penalties
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Automatic revocation of tax-exempt status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Failure to file Form 990, 990-EZ, or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-PF
                  </Link>{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    for three consecutive years
                  </span>{' '}
                  results in automatic loss of exemption. Organizations must
                  reapply to regain status.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Criminal penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Willful failure to file, fraudulent reporting, or false
                  statements may lead to fines or imprisonment under IRS code
                  provisions. These apply only in serious non-compliance cases.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  State-level penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  States may impose additional penalties for failure to meet
                  local filing requirements.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="relief"
                >
                  Penalty Relief and Exceptions
                </Typography>
                <Typography variant="body1">
                  The IRS may waive penalties if the organization demonstrates
                  reasonable cause, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Natural disasters or unforeseen events</li>
                  <li>Serious illness or death of key personnel </li>
                  <li>Inability to obtain records despite reasonable effort</li>
                  <li>Reliance on incorrect IRS guidance</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Requests must include a written explanation signed under
                  penalty of perjury, along with corrective actions taken.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="filing"
                >
                  Filing Form 990 with TaxZerone to Avoid Penalties
                </Typography>
                <Typography variant="body1">
                  Filing Form 990 on time with accurate information is key to
                  avoiding penalties and maintaining tax-exempt status.
                  TaxZerone simplifies the process with a user-friendly
                  platform, guided steps, and timely reminders to ensure smooth
                  compliance. It also offers automatic schedule inclusion, IRS
                  validation checks, free corrections for rejected returns, and
                  an easy-to-use interface for error-free filing.
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 4,
                    listStyleType: 'none',
                    '& li': { mb: 1.2, ml: -2.5 },
                  }}
                >
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Step-by-step guidance for each section. If you want to learn
                    more, see{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990 instructions
                    </Link>
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Simplified filing support for small nonprofits. See{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-ez-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-EZ instructions
                    </Link>
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Guidance for unrelated business income tax reporting.{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-t-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-T instructions
                    </Link>
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Instructions for private foundation filings and compliance.
                    learn more, see{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-pf-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-PF instructions
                    </Link>
                  </li>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Need more time to file?
                  </span>{' '}
                  You can request an automatic 6-month extension to avoid late
                  penalties. See{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8868
                  </Link>{' '}
                  for more details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Need to confirm deadlines?
                  </span>{' '}
                  You can check the{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990 due dates
                  </Link>{' '}
                  section anytime to verify filing timelines and avoid late
                  penalties.
                </Typography>

                <Alert
                  severity="info"
                  id="sources"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        of Form 990,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of 990-EZ,
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990pf.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        of 990-PF, and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990t.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of 990-T.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need to File Your Form 990?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Don't risk fines or losing your tax-exempt status. File your Form
              990 accurately
              <br /> and on time with TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=penalties`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default PenaltiesNonprofit;
